import { ApolloQueryResult } from '@apollo/client';
import LoadingButton from '@atlaskit/button/loading-button';
import EmptyState from '@atlaskit/empty-state';
import Lozenge from '@atlaskit/lozenge';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import { generatedTypesNew } from '@css-xp/graphtypes';
import {
  CreditCard,
  InvoiceGroup,
  Maybe,
  PaymentMethod,
  PaymentMethodType,
  PaymentOnTerms,
  TermPaymentTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import { setDefaultPaymentMethod } from '@atlassian/temp-proxies';
import { CREDIT_DETAILS, Permissions, Resources } from '@atlassian/cssxp-typescommon';
import { ENABLE_CREDIT_MANAGEMENT, ENABLE_TXA_REMOVE_PAYMENT_METHOD } from '@atlassian/cssxp-utils-generic';
import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import OpenSidebarButton from '../../application/src/sidebar/open-sidebar-button';
import { useFeatureToggle, useNotification } from '../../hooks';
import { NotificationTypes } from '../../notifications';
import { errorNotificationBuilder, successNotificationBuilder } from '../../notifications/utils/notification-builder';
import { OpenSidebarLink } from '../../open-sidebar-link';
import { isAccess, isResourceAccess } from '../../security';
import TermPaymentMethod from '../../term-payment-method/term-payment-method';
import './payment-methods-table-v2.scss';

export enum SIDEBAR {
  // eslint-disable-next-line no-unused-vars
  VIEW_LINKED_SUBSCRIPTIONS = 'viewLinkedSubscriptions',
  REMOVE_PAYMENT_METHOD = 'removePaymentMethod',
}

interface PaymentMethodTableProps {
  transactionAccountId: string;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  cardPaymentMethods: PaymentMethod[];
  invoiceGroupId?: string;
  loading: boolean;
  isPaymentMethodDisabled: boolean;
  defaultPaymentMethodId: string | null;
  availableInvoiceGroups?: Maybe<InvoiceGroup[]>;
  // This sets the context for this component, so that we can use this to set additional payment method at Transaction a/c
  // or non Transaction a/c level(e.g -> Invoice Group etc.) both.
  forTransactionAccount?: boolean;
  selectPaymentMethod?: () => void;
  selectedPaymentMethodId?: string;
  transactionAccountDisplayName?: string;
  transactionAccountNumber?: string;
  allPaymentMethods?: PaymentMethod[] | PaymentOnTerms[];
  currentTermAccountStatus?: generatedTypesNew.Maybe<string> | undefined;
}

interface GetPaymentMethodOptionsArgs {
  paymentMethod: PaymentMethod;
  index: number;
  defaultPaymentMethodId: string | undefined | null;
  isEnableCreditManagement?: boolean;
  currentTermAccountStatus?: generatedTypesNew.Maybe<string> | undefined;
}

interface getPaymentDetailsProps {
  paymentMethod: PaymentMethod;
  transactionAccountId: string;
  defaultPaymentMethodId: string | null;
  availableInvoiceGroups?: Maybe<InvoiceGroup[]>;
}

const { PAYMENT_METHOD_DEFAULT_WRITE } = Permissions;

export const getPaymentMethodOption = ({
  paymentMethod,
  index,
  defaultPaymentMethodId,
  isEnableCreditManagement = false,
  currentTermAccountStatus,
}: GetPaymentMethodOptionsArgs): ReactElement<typeof PaymentMethodPanel> | undefined => {
  if (paymentMethod.type === PaymentMethodType.CARD) {
    const creditCard = paymentMethod as CreditCard;

    // convert expMonth & expYear to Numbers
    const expMonth = +(creditCard.card?.expirationMonth ?? 0);
    const expYear = +(creditCard.card?.expirationYear ?? 0);
    const card = {
      brand: creditCard.card?.brand ?? '',
      expMonth,
      expYear,
      last4: creditCard.card?.last4Numbers ?? '',
      name: creditCard.card?.cardHolderName ?? '',
    };

    return (
      <div className="payment-method-panel-container">
        <PaymentMethodPanel
          key={`paymethod-${index}`}
          paymentMethod={{
            id: creditCard.id as PaymentMethodId,
            type: paymentMethod.type,
            card,
            default: defaultPaymentMethodId === creditCard.id,
          }}
        />
      </div>
    );
  } else if (isEnableCreditManagement && paymentMethod.type === PaymentMethodType.DEFERRED) {
    const paymentOnTerms = paymentMethod as PaymentOnTerms;
    return (
      <div className="payment-method-panel-container">
        <TermPaymentMethod
          paymentNetType={
            paymentOnTerms?.terms?.invoiceDueDays === 30 ? TermPaymentTypes.NET30 : TermPaymentTypes.NET14
          }
          totalLimit={paymentOnTerms?.totalCreditLimit as number}
          currentTermAccountStatus={currentTermAccountStatus}
        />
      </div>
    );
  }
  return;
};

const wrapInRowItem = (node: ReactElement | undefined | null | boolean, elementId = '') => {
  return (
    <div className="row-item" id={elementId}>
      {node}
    </div>
  );
};

const makeSuccessNotificationOptions = (last4Numbers: string) => {
  return successNotificationBuilder()
    .isVersion(NotificationTypes.FLAG)
    .withId('update-billto-party-success')
    .withMessage(`Successfully set credit card **** **** **** ${last4Numbers} as default payment method`)
    .withDismissAfter(5000)
    .build();
};

const errorNotificationOptions = errorNotificationBuilder()
  .withId('update-billto-party-error')
  .withMessage('Error setting default payment method')
  .withDismissAfter(5000)
  .build();

const getPaymentDetails = ({
  paymentMethod,
  transactionAccountId,
  defaultPaymentMethodId,
  availableInvoiceGroups,
}: getPaymentDetailsProps) => {
  return (
    <div className="payment-details-block">
      {wrapInRowItem(<p className="payment-methods-header">Payment details</p>)}
      {wrapInRowItem(
        paymentMethod.hasLinkedSubscriptions && (
          <OpenSidebarLink
            sidebar={SIDEBAR.VIEW_LINKED_SUBSCRIPTIONS}
            payload={{
              paymentMethod,
              transactionAccountId,
              defaultPaymentMethodId,
              availableInvoiceGroups,
            }}
            appearance="link"
            sidebarOptions={{
              sidebarWidth: '968px',
              isOverlay: true,
            }}
            testId="view-linked-subscriptions-action"
            spacing="compact"
            className="view-subscriptions-link"
            label="View linked subscriptions and invoices"
          >
            View linked subscriptions and invoices
          </OpenSidebarLink>
        )
      )}
      {wrapInRowItem(paymentMethod.default && <Lozenge isBold>DEFAULT</Lozenge>)}
    </div>
  );
};

const PaymentMethodsTableTxa = ({
  transactionAccountId,
  invoiceGroupId,
  cardPaymentMethods,
  refetch,
  defaultPaymentMethodId,
  availableInvoiceGroups,
  transactionAccountDisplayName,
  transactionAccountNumber,
  allPaymentMethods,
  currentTermAccountStatus,
}: PaymentMethodTableProps) => {
  const [defaultPaymentBeingSetMap, setIsDefaultPaymentBeingSet] = React.useState(new Map<string, boolean>());
  const [isEnabled] = useFeatureToggle();

  const isRemovePaymentMethodEnabled = isEnabled(ENABLE_TXA_REMOVE_PAYMENT_METHOD);
  const isEnableCreditManagement = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const isCardPaymentAvailable = Boolean(cardPaymentMethods?.length);
  const areAllPaymentMethodAvailable = Boolean(allPaymentMethods?.length);
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();

  React.useEffect(() => {
    const map: Map<string, boolean> = cardPaymentMethods.reduce((acc, paymentMethod) => {
      acc.set(paymentMethod.id, false);
      return acc;
    }, new Map());
    setIsDefaultPaymentBeingSet(map);
  }, [cardPaymentMethods]);

  const setDefaultPaymentMethodStatus = (paymentMethodId: string | undefined | null, status: boolean) => {
    if (paymentMethodId) {
      defaultPaymentBeingSetMap.set(paymentMethodId, status);
      setIsDefaultPaymentBeingSet(new Map(defaultPaymentBeingSetMap));
    }
  };

  const notify = ({
    paymentMethod = null,
    errored = false,
  }: {
    paymentMethod?: CreditCard | null;
    errored?: boolean;
  }) => {
    if (errored) {
      sendErrorNotification(errorNotificationOptions);
      setNotificationSent(true);
    } else if (!notificationSent) {
      sendSuccessNotification(makeSuccessNotificationOptions(paymentMethod?.card?.last4Numbers ?? ''));
      setNotificationSent(true);
    }
  };

  const onSetDefaultPaymentClicked = async (paymentMethod: PaymentMethod) => {
    setDefaultPaymentMethodStatus(paymentMethod.id, true);
    if (paymentMethod && paymentMethod.id) {
      try {
        await setDefaultPaymentMethod({
          transactionAccountId,
          id: paymentMethod.id,
          invoiceGroupId,
        });
        notify({ paymentMethod });
        refetch();
      } catch (err) {
        notify({ errored: true });
      } finally {
        setDefaultPaymentMethodStatus(paymentMethod.id, false);
      }
    }
  };

  return (
    <div className="payment-methods-container" data-testid="payment-methods-table-v2">
      <div className="payment-methods-table">
        <p className="payment-methods-header">Payment methods</p>
        {!isEnableCreditManagement &&
          isCardPaymentAvailable &&
          cardPaymentMethods.map((paymentMethod, index) => {
            return (
              paymentMethod.type === PaymentMethodType.CARD && (
                <div className="payment-method-row" key={`payment-method-${index}`}>
                  {wrapInRowItem(
                    getPaymentMethodOption({
                      paymentMethod,
                      index,
                      defaultPaymentMethodId,
                      isEnableCreditManagement,
                      currentTermAccountStatus,
                    })
                  )}
                  {wrapInRowItem(
                    paymentMethod.default ? (
                      <Lozenge isBold>DEFAULT</Lozenge>
                    ) : (
                      <LoadingButton
                        appearance="primary"
                        spacing="compact"
                        aria-label="Set as default"
                        isLoading={defaultPaymentBeingSetMap.get(paymentMethod.id as string)}
                        onClick={() => onSetDefaultPaymentClicked(paymentMethod)}
                        isDisabled={
                          !(
                            isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
                            isAccess(PAYMENT_METHOD_DEFAULT_WRITE)
                          )
                        }
                      >
                        Set as default
                      </LoadingButton>
                    )
                  )}
                  {wrapInRowItem(
                    paymentMethod.hasLinkedSubscriptions && (
                      <OpenSidebarLink
                        sidebar={SIDEBAR.VIEW_LINKED_SUBSCRIPTIONS}
                        payload={{
                          paymentMethod,
                          transactionAccountId,
                          defaultPaymentMethodId,
                          availableInvoiceGroups,
                        }}
                        appearance="link"
                        sidebarOptions={{
                          sidebarWidth: '968px',
                          isOverlay: true,
                        }}
                        testId="view-linked-subscriptions-action"
                        spacing="compact"
                        className="view-subscriptions-link"
                        label="View linked subscriptions and invoices"
                      >
                        View linked subscriptions and invoices
                      </OpenSidebarLink>
                    )
                  )}{' '}
                  {wrapInRowItem(paymentMethod?.paymentIssue && <Lozenge appearance="removed">PAYMENT ERROR</Lozenge>)}
                  {wrapInRowItem(
                    <OpenSidebarButton
                      testId="remove-payment"
                      isDisabled={!isRemovePaymentMethodEnabled}
                      payload={{
                        paymentMethod: paymentMethod,
                        transactionAccountId,
                        defaultPaymentMethodId,
                        availableInvoiceGroups,
                        transactionAccountDisplayName,
                        transactionAccountNumber,
                        refetch,
                      }}
                      appearance="primary"
                      sidebar={SIDEBAR.REMOVE_PAYMENT_METHOD}
                      label="Remove"
                    >
                      Remove
                    </OpenSidebarButton>
                  )}
                </div>
              )
            );
          })}
        {isEnableCreditManagement &&
          areAllPaymentMethodAvailable &&
          allPaymentMethods?.map((paymentMethod, index) => {
            return (
              <div className="payment-method-row" key={`payment-method-${index}`}>
                {wrapInRowItem(
                  getPaymentMethodOption({
                    paymentMethod,
                    index,
                    defaultPaymentMethodId,
                    isEnableCreditManagement,
                    currentTermAccountStatus,
                  })
                )}
                {wrapInRowItem(
                  getPaymentDetails({
                    paymentMethod,
                    transactionAccountId,
                    defaultPaymentMethodId,
                    availableInvoiceGroups,
                  }),
                  'payment-details'
                )}
                {wrapInRowItem(paymentMethod?.paymentIssue && <Lozenge appearance="removed">PAYMENT ERROR</Lozenge>)}
                {wrapInRowItem(
                  paymentMethod.type === PaymentMethodType.CARD && !paymentMethod.default && (
                    <LoadingButton
                      appearance="link"
                      spacing="compact"
                      aria-label="Set as default"
                      isLoading={defaultPaymentBeingSetMap.get(paymentMethod.id as string)}
                      onClick={() => onSetDefaultPaymentClicked(paymentMethod)}
                      isDisabled={
                        !(isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(PAYMENT_METHOD_DEFAULT_WRITE))
                      }
                    >
                      Set as default
                    </LoadingButton>
                  )
                )}
                {wrapInRowItem(
                  paymentMethod.type === PaymentMethodType.CARD ? (
                    <OpenSidebarButton
                      testId="remove-payment"
                      isDisabled={!isRemovePaymentMethodEnabled}
                      payload={{
                        paymentMethod: paymentMethod,
                        transactionAccountId,
                        defaultPaymentMethodId,
                        availableInvoiceGroups,
                        transactionAccountDisplayName,
                        transactionAccountNumber,
                        refetch,
                      }}
                      appearance="primary"
                      sidebar={SIDEBAR.REMOVE_PAYMENT_METHOD}
                      label="Remove"
                    >
                      Remove
                    </OpenSidebarButton>
                  ) : (
                    <Link to={`${CREDIT_DETAILS}/${transactionAccountId}`} target="_blank">
                      View details
                    </Link>
                  )
                )}
              </div>
            );
          })}
        {((!isEnableCreditManagement && !isCardPaymentAvailable) ||
          (isEnableCreditManagement && !areAllPaymentMethodAvailable)) && (
          <EmptyState header="We couldn't find any payment methods"></EmptyState>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodsTableTxa;
